/**
 * Saved design templates and recent codes. Both live in localStorage only —
 * nothing about a user's QR content ever leaves the browser.
 */

const TEMPLATES_KEY = 'sohozkaj.qr.templates';
const HISTORY_KEY = 'sohozkaj.qr.history';

const MAX_TEMPLATES = 8;
const MAX_HISTORY = 10;

/** Style fields a template restores; content and logo art are deliberately excluded. */
const STYLE_KEYS = [
    'fgColor', 'bgColor', 'transparentBg',
    'gradientEnabled', 'gradientType', 'gradientFrom', 'gradientTo', 'gradientRotation',
    'dotStyle', 'eyeFrameStyle', 'eyeBallStyle',
    'customEyeColors', 'eyeFrameColor', 'eyeBallColor',
    'errorLevel', 'quietZonePercent', 'cornerRadiusPercent', 'bgPattern',
    'frameType', 'frameLabel', 'frameBorderColor', 'frameLabelBgColor', 'frameLabelTextColor', 'frameBgColor',
    'logoSizePercent', 'logoMarginPercent', 'logoRadiusPercent', 'logoPlate', 'hideBackgroundDots',
];

function read(key, fallback) {
    try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : fallback;
    } catch {
        return fallback;
    }
}

function write(key, value) {
    try {
        localStorage.setItem(key, JSON.stringify(value));
        return true;
    } catch {
        // Quota or private mode — the feature degrades, the tool keeps working.
        return false;
    }
}

/** Style-only snapshot of a design, safe to store and to re-apply later. */
export function styleSnapshot(design) {
    const snap = {};
    STYLE_KEYS.forEach((key) => { snap[key] = design[key]; });
    return snap;
}

export function loadTemplates() {
    const list = read(TEMPLATES_KEY, []);
    return Array.isArray(list) ? list : [];
}

export function saveTemplate(design, name) {
    const list = loadTemplates();
    const next = [...list, { id: Date.now(), name, style: styleSnapshot(design) }].slice(-MAX_TEMPLATES);
    write(TEMPLATES_KEY, next);
    return next;
}

export function deleteTemplate(id) {
    const next = loadTemplates().filter((item) => item.id !== id);
    write(TEMPLATES_KEY, next);
    return next;
}

export function loadHistory() {
    const list = read(HISTORY_KEY, []);
    return Array.isArray(list) ? list : [];
}

/**
 * Remember a generated code. Same payload twice keeps one entry, moved to the
 * front, so downloading a few sizes of one code does not flood the list.
 */
export function saveHistoryEntry({ type, payload, fields, design, thumbnail }) {
    if (!payload) return loadHistory();
    const entry = {
        id: Date.now(),
        type,
        payload,
        fields,
        style: styleSnapshot(design),
        thumbnail,
        ts: Date.now(),
    };
    const next = [entry, ...loadHistory().filter((item) => item.payload !== payload)].slice(0, MAX_HISTORY);
    write(HISTORY_KEY, next);
    return next;
}

export function deleteHistoryEntry(id) {
    const next = loadHistory().filter((item) => item.id !== id);
    write(HISTORY_KEY, next);
    return next;
}

export function clearHistory() {
    write(HISTORY_KEY, []);
    return [];
}
